import { queryClient } from '@/queryClient';

/**
 * Invalidação de cache depois que um plano do assistente é aplicado.
 *
 * As telas do app são alimentadas por react-query, e o assistente escreve por
 * fora delas — sem isso, quem estivesse com a lista de redes (ou de VPNs, ou de
 * appliances) aberta continuaria vendo o estado anterior até um F5.
 *
 * As chaves são prefixos: `invalidateQueries(['vpns'])` no react-query v3 pega
 * `['vpns', applianceId]` também, então não é preciso repetir o id.
 */

// Dispositivos e grupos aparecem em várias telas, cada uma com a sua chave.
const CHAVES_DISPOSITIVO = ['lan_dispositivo', 'lan_dispositivo-all', 'dispositivos', 'appliance_devices'];
const CHAVES_GRUPO_DISPOSITIVO = [
    'grupo_dispositivo',
    'grupos-dispositivo',
    'grupo_dispositivo_all',
    'grupo_dispositivo_sidebar',
];
// Regra de ASN pode criar o registro do ASN — por isso `fw_asns` entra junto.
const CHAVES_FIREWALL = ['fw_policies', 'fw-policies', 'fw_policies_sidebar', 'fw_rules', 'fw_asns'];
const CHAVES_FIREWALL_ENTRADA = ['fw_inbound_policies', 'fw_asns'];

/** Chaves afetadas por cada ferramenta de escrita do plano. */
const CHAVES_POR_FERRAMENTA: Record<string, string[]> = {
    criar_rede_lan: ['appliance-redes', 'lan-status', 'appliance'],
    editar_rede_lan: ['appliance-redes', 'lan-status', 'appliance'],
    remover_rede_lan: ['appliance-redes', 'lan-status', 'appliance'],

    criar_rede_wan: ['appliance-redes', 'wan-status', 'appliance'],
    editar_rede_wan: ['appliance-redes', 'wan-status', 'appliance'],
    remover_rede_wan: ['appliance-redes', 'wan-status', 'appliance'],

    criar_vpn: ['vpns', 'vpn-status'],
    remover_vpn: ['vpns', 'vpn-status'],

    criar_ipsec: ['ipsec-status', 'appliance-redes'],
    remover_ipsec: ['ipsec-status', 'appliance-redes'],

    criar_nat: ['nat_port_forward'],
    remover_nat: ['nat_port_forward'],

    criar_routing_redirect: ['routing-redirect'],
    remover_routing_redirect: ['routing-redirect'],

    remover_hotspot: ['hotspot-list', 'hotspot-config', 'appliance-redes'],

    criar_dispositivo: [...CHAVES_DISPOSITIVO, ...CHAVES_GRUPO_DISPOSITIVO],
    editar_dispositivo: CHAVES_DISPOSITIVO,
    remover_dispositivo: [...CHAVES_DISPOSITIVO, ...CHAVES_GRUPO_DISPOSITIVO, 'routing-redirect'],

    criar_grupo_dispositivos: CHAVES_GRUPO_DISPOSITIVO,
    editar_grupo_dispositivos: CHAVES_GRUPO_DISPOSITIVO,
    remover_grupo_dispositivos: [...CHAVES_GRUPO_DISPOSITIVO, ...CHAVES_FIREWALL],
    adicionar_dispositivos_grupo: CHAVES_GRUPO_DISPOSITIVO,
    remover_dispositivos_grupo: CHAVES_GRUPO_DISPOSITIVO,

    criar_politica_firewall: CHAVES_FIREWALL,
    editar_politica_firewall: CHAVES_FIREWALL,
    remover_politica_firewall: CHAVES_FIREWALL,
    criar_politica_entrada: CHAVES_FIREWALL_ENTRADA,
    editar_politica_entrada: CHAVES_FIREWALL_ENTRADA,
    remover_politica_entrada: CHAVES_FIREWALL_ENTRADA,
    adicionar_regras_firewall: CHAVES_FIREWALL,
    editar_regra_firewall: CHAVES_FIREWALL,
    remover_regras_firewall: CHAVES_FIREWALL,
};

/**
 * Chaves que dependem da appliance como um todo. Sempre invalidadas junto — os
 * contadores da sidebar e o seletor de appliance derivam delas.
 */
const CHAVES_GLOBAIS = ['appliances', 'appliance-box', 'appliances-list-scope'];

/**
 * Invalida o cache das telas afetadas pelas operações aplicadas.
 *
 * Remover uma appliance mexe em praticamente tudo (redes, VPNs, regras,
 * telemetria), então nesse caso vale limpar o cache inteiro em vez de tentar
 * enumerar.
 */
export function invalidarPorOperacoes(ferramentas: string[]): void {
    if (ferramentas.includes('remover_appliance')) {
        void queryClient.invalidateQueries();
        return;
    }

    const chaves = new Set<string>(CHAVES_GLOBAIS);
    for (const ferramenta of ferramentas) {
        for (const chave of CHAVES_POR_FERRAMENTA[ferramenta] ?? []) {
            chaves.add(chave);
        }
    }

    for (const chave of chaves) {
        void queryClient.invalidateQueries([chave]);
    }
}
